import { useRef, useEffect, useState } from 'react';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import clsx from 'clsx';
import type { POI } from '@poi-planner/shared';
import { FOCUS_RADIUS_KM } from '@poi-planner/shared';
import { useUIStore } from '../store/uiStore';
import { dndIds } from '../utils/dndIds';
import type { TripSidebarController } from '../hooks/useTripSidebarController';
import { PoiFilterBar } from './PoiFilterBar';
import { SidebarPoiCard } from './SidebarPoiCard';

interface SidebarProps {
  controller: TripSidebarController;
  /** Callback when POI is selected (for map focus in day focus view) */
  onSelectPoi?: (poiId: string | null) => void;
  /** Whether bounds filter is enabled */
  boundsFilterEnabled?: boolean;
  /** Callback when bounds filter changes */
  onBoundsFilterChange?: (enabled: boolean) => void;
}

export function Sidebar({
  controller,
  onSelectPoi,
  boundsFilterEnabled,
  onBoundsFilterChange,
}: SidebarProps) {
  const {
    hideScheduled,
    setHideScheduled,
    focusModeEnabled,
    setFocusModeEnabled,
    targetPoiId,
    setTargetPoiId,
    targetPoi,
    search,
    sortMode,
    sortAscending,
    tagFilters,
    sourceFilters,
    collectionFilters,
    allTags,
    filteredPOIs,
    distanceMap,
    allSourceTexts,
    activeFilterCounts,
    collectionOptions,
    scheduledCounts,
    hasScheduledCoords,
    tripFilterEnabled,
    setTripFilterEnabled,
    pinnedPoiIds,
    onTogglePin,
    setSearch,
    setSortMode,
    setSortAscending,
    toggleTag,
    toggleSource,
    toggleCollection,
    clearAllFilters,
    totalPoiCount,
  } = controller;

  const openPOIEditor = useUIStore((s) => s.openPOIEditor);
  const { setNodeRef, isOver } = useDroppable({ id: dndIds.sidebar });

  return (
    <aside
      ref={setNodeRef}
      className={clsx(
        'sidebar transition-colors',
        isOver && 'bg-green-50'
      )}
    >
      <div className="p-6 border-b border-border">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold">
            {hideScheduled ? `${filteredPOIs.length} Unscheduled` : 'All POIs'}
          </h3>
          <button onClick={() => openPOIEditor('create')} className="btn-primary">
            <span className="material-symbols-outlined text-base">add</span>
            Create
          </button>
        </div>

        <PoiFilterBar
          search={search}
          sortMode={sortMode}
          sortAscending={sortAscending}
          tagFilters={tagFilters}
          allTags={allTags}
          onSearchChange={setSearch}
          onSortModeChange={setSortMode}
          onSortAscendingChange={setSortAscending}
          onToggleTag={toggleTag}
          sourceFilters={sourceFilters}
          allSourceTexts={allSourceTexts}
          onToggleSource={toggleSource}
          collectionFilters={collectionFilters}
          collections={collectionOptions}
          onToggleCollection={toggleCollection}
          hideScheduled={hideScheduled}
          onHideScheduledChange={setHideScheduled}
          focusModeEnabled={focusModeEnabled}
          onFocusModeChange={setFocusModeEnabled}
          targetPoi={targetPoi}
          onClearTarget={() => setTargetPoiId(null)}
          boundsFilterEnabled={boundsFilterEnabled}
          onBoundsFilterChange={onBoundsFilterChange}
          tripFilter={tripFilterEnabled}
          onTripFilterToggle={setTripFilterEnabled}
          onClearAllFilters={clearAllFilters}
          activeFilterCounts={activeFilterCounts}
        />
      </div>

      <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3 custom-scrollbar">
        {filteredPOIs.length === 0 ? (
          <div className="empty-state py-12 px-6">
            <span className={clsx(
              'material-symbols-outlined text-5xl',
              hideScheduled && totalPoiCount > 0 ? 'text-green-500' :
              focusModeEnabled && totalPoiCount > 0 ? 'text-emerald-500' : 'text-gray-300'
            )}>
              {totalPoiCount === 0 ? 'explore' :
               hideScheduled ? 'check_circle' :
               focusModeEnabled ? 'location_off' : 'search_off'}
            </span>
            <div>
              <p className="text-sm font-semibold text-text-secondary mb-1">
                {totalPoiCount === 0
                  ? 'No POIs yet'
                  : hideScheduled ? 'All POIs scheduled!'
                  : focusModeEnabled && !hasScheduledCoords ? 'No scheduled items yet'
                  : focusModeEnabled ? 'No POIs nearby'
                  : 'No matches'}
              </p>
              <p className="text-xs text-text-muted leading-relaxed">
                {totalPoiCount === 0
                  ? 'Create a point of interest to start planning your trip'
                  : hideScheduled
                    ? "Toggle 'Hide planned' off to see all POIs"
                    : focusModeEnabled && !hasScheduledCoords
                      ? 'Schedule some POIs first, then focus mode will filter by proximity'
                      : focusModeEnabled
                        ? `No POIs within ${FOCUS_RADIUS_KM}km of scheduled locations`
                        : 'Try adjusting your search or tag filters'}
              </p>
            </div>
            {totalPoiCount === 0 && (
              <button
                onClick={() => openPOIEditor('create')}
                className="btn-primary px-4 py-2 text-[13px]"
              >
                <span className="material-symbols-outlined text-base">add</span>
                Create POI
              </button>
            )}
          </div>
        ) : (
          filteredPOIs.map((poi) => (
            <DraggableSidebarPOICard
              key={poi.id}
              poi={poi}
              scheduledCount={scheduledCounts.get(poi.id) ?? 0}
              distance={distanceMap.get(poi.id)}
              showDistance={!!targetPoiId}
              isTargeted={poi.id === targetPoiId}
              onSetTarget={onSelectPoi ?? setTargetPoiId}
              isPinned={pinnedPoiIds.has(poi.id)}
              isScheduled={(scheduledCounts.get(poi.id) ?? 0) > 0}
              onTogglePin={onTogglePin}
            />
          ))
        )}
      </div>
    </aside>
  );
}

interface DraggableSidebarPOICardProps {
  poi: POI;
  scheduledCount: number;
  distance?: number;
  showDistance: boolean;
  isTargeted?: boolean;
  onSetTarget?: (poiId: string | null) => void;
  isPinned?: boolean;
  isScheduled?: boolean;
  onTogglePin?: (poiId: string) => void;
}

function DraggableSidebarPOICard({
  poi,
  scheduledCount,
  distance,
  showDistance,
  isTargeted,
  onSetTarget,
  isPinned,
  isScheduled,
  onTogglePin,
}: DraggableSidebarPOICardProps) {
  const openPOIEditor = useUIStore((s) => s.openPOIEditor);
  const wasDragging = useRef(false);
  const [pinMessage, setPinMessage] = useState<string | null>(null);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dndIds.sidebarItem(poi.id),
    data: { type: 'sidebar-poi', poiId: poi.id },
  });

  useEffect(() => {
    if (isDragging) wasDragging.current = true;
  }, [isDragging]);

  useEffect(() => {
    if (!pinMessage) return;
    const t = setTimeout(() => setPinMessage(null), 2500);
    return () => clearTimeout(t);
  }, [pinMessage]);

  const handleClick = () => {
    if (wasDragging.current) {
      wasDragging.current = false;
      return;
    }
  };

  return (
    <div className="relative">
      <SidebarPoiCard
        poi={poi}
        scheduledCount={scheduledCount}
        distance={distance}
        showDistance={showDistance}
        isTargeted={isTargeted}
        onSetTarget={onSetTarget}
        isDragging={isDragging}
        onClick={handleClick}
        onEdit={() => openPOIEditor('edit', poi.id)}
        dndRef={setNodeRef}
        dndAttributes={attributes}
        dndListeners={listeners}
        isPinned={isPinned}
        isScheduled={isScheduled}
        onTogglePin={onTogglePin}
        onShowPinMessage={setPinMessage}
      />
      {pinMessage && (
        <div className="absolute -bottom-1 left-0 right-0 translate-y-full z-10 pointer-events-none">
          <div className="mx-2 mt-1 px-2.5 py-1.5 bg-slate-800 text-white text-[11px] rounded-lg shadow-lg text-center">
            {pinMessage}
          </div>
        </div>
      )}
    </div>
  );
}
